document.addEventListener("DOMContentLoaded", () => {
  // Update packaging variants
  // Flag to prevent overlapping updates
  let isUpdatingPackaging = false;
  let lastPackagingUpdate = 0;
  let loadingOverlayTimeout = null;
  let pendingCartUpdates = 0;
  let cartCache = null;
  let cartCacheAt = 0;
  let cartRequestInFlight = null;

  const CART_TTL_MS = 400;

  async function getCart({ force = false } = {}) {
    const now = Date.now();

    if (!force && cartCache && now - cartCacheAt < CART_TTL_MS) {
      return cartCache;
    }

    if (!force && cartRequestInFlight) {
      return cartRequestInFlight;
    }

    cartRequestInFlight = fetch("/cart.js")
      .then((res) => res.json())
      .then((cart) => {
        cartCache = cart;
        cartCacheAt = Date.now();
        return cart;
      })
      .finally(() => {
        cartRequestInFlight = null;
      });

    return cartRequestInFlight;
  }

  function invalidateCartCache() {
    cartCache = null;
    cartCacheAt = 0;
  }

  // Create loading overlay if it doesn't exist
  function ensureLoadingOverlay() {
    if (!document.getElementById("cart-loading-overlay")) {
      const overlay = document.createElement("div");
      overlay.id = "cart-loading-overlay";
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: none;
        justify-content: center;
        align-items: center;
        z-index: 9999;
      `;
      overlay.innerHTML = `
        <div style="
          background: white;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
        ">
          <div class="loading-spinner" style="
            width: 40px;
            height: 40px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #3498db;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 10px;
          "></div>
          <p>Updating cart...</p>
        </div>
      `;
      document.body.appendChild(overlay);
    }
  }

  // Show loading overlay
  function showLoadingOverlay() {
    if (pendingCartUpdates === 0) {
      ensureLoadingOverlay();
      const overlay = document.getElementById("cart-loading-overlay");
      overlay.style.display = "flex";

      // Clear any existing timeout
      if (loadingOverlayTimeout) {
        clearTimeout(loadingOverlayTimeout);
      }

      // Set a timeout to hide the overlay after 5 seconds (safety measure)
      loadingOverlayTimeout = setTimeout(() => {
        hideLoadingOverlay();
      }, 5000);
    }
    pendingCartUpdates++;
  }

  // Hide loading overlay
  function hideLoadingOverlay() {
    pendingCartUpdates--;
    if (pendingCartUpdates <= 0) {
      const overlay = document.getElementById("cart-loading-overlay");
      if (overlay) {
        overlay.style.display = "none";
      }
      if (loadingOverlayTimeout) {
        clearTimeout(loadingOverlayTimeout);
        loadingOverlayTimeout = null;
      }
      pendingCartUpdates = 0;
    }
  }

  // Constants
  const PRODUCTS = {
    GIFT_BOX: {
      id: 8337456988248,
      variants: {
        xsmall: 43340760121432,
        small: 42796363546712,
        large: 42796363579480,
      },
      thresholds: [3084.43, 4944.157], // 7.0 lbs and 11.0 lbs in grams
    },
    COOLER: {
      id: 8337471144024,
      variants: {
        xsmall: 43186618040408,
        small: 42796396642392,
        medium: 42796396675160,
        large: 42796396707928,
      },
      thresholds: [1356.241, 4077.795, 7252.942],
    },
  };

  // Maximum weight limit in grams (22.99 lbs)
  const MAX_WEIGHT_LIMIT = 10201.29;

  /*
	// Helper function to update checkout button state
	function updateCheckoutButtonState(adjustedWeightGrams) {
		const checkoutButton = document.querySelector('[name="checkout"]');
		if (checkoutButton) {
			const isOverweight = adjustedWeightGrams > MAX_WEIGHT_LIMIT;
			checkoutButton.disabled = isOverweight;
			checkoutButton.style.opacity = isOverweight ? '0.5' : '1';
			if (isOverweight) {
				// Max cart-weight messaging is handled by the Maxminify plugin.
				// checkoutButton.title =
				// 	'Order weight exceeds maximum limit of 22 lbs';
				checkoutButton.title = '';
				checkoutButton.textContent = 'Reduce cart weight';

				// Create a MutationObserver to maintain the button text
				if (!checkoutButton._observer) {
					checkoutButton._observer = new MutationObserver(
						(mutations) => {
							mutations.forEach((mutation) => {
								if (
									mutation.type === 'characterData' ||
									mutation.type === 'childList'
								) {
									if (
										checkoutButton.textContent !==
										'Reduce cart weight'
									) {
										checkoutButton.textContent =
											'Reduce cart weight';
									}
								}
							});
						}
					);

					checkoutButton._observer.observe(checkoutButton, {
						characterData: true,
						childList: true,
						subtree: true,
					});
				}

				// Disabled to avoid duplicate max cart-weight messages.
				// if (!sessionStorage.getItem('weightAlertShown')) {
				// 	alert(
				// 		'Your cart weight exceeds the maximum allowed of 22 lbs. per order. Please remove items to fit within the cart limits or create a second order.'
				// 	);
				// 	sessionStorage.setItem('weightAlertShown', 'true');
				// }
			} else {
				checkoutButton.title = '';
				checkoutButton.textContent = 'Checkout';
				// Disconnect observer if it exists
				if (checkoutButton._observer) {
					checkoutButton._observer.disconnect();
					delete checkoutButton._observer;
				}
				sessionStorage.removeItem('weightAlertShown');
			}
		}

		// Update weight display in header
		updateWeightDisplay(adjustedWeightGrams);
	}
	*/

  // Helper function to update weight display in header
  function updateWeightDisplay(weightGrams) {
    const weightLbs = (weightGrams / 453.592).toFixed(1); // Convert grams to pounds
    const weightSpan = document.querySelector(".site-header .lbs-span");
    if (weightSpan) {
      weightSpan.textContent = `${weightLbs} lbs`;
    }
  }

  // Helper function to determine new variant IDs
  function determineNewVariants(cart, adjustedWeightGrams) {
    const giftBox = cart.items.find(
      (item) => item.product_id === PRODUCTS.GIFT_BOX.id,
    );
    const cooler = cart.items.find(
      (item) => item.product_id === PRODUCTS.COOLER.id,
    );
    const updates = [];

    if (giftBox) {
      let newGiftBoxVariant = null;
      if (cart.items.length === 1 && giftBox.quantity === 1) {
        // If only gift box in cart, use xsmall
        if (giftBox.variant_id !== PRODUCTS.GIFT_BOX.variants.xsmall) {
          newGiftBoxVariant = PRODUCTS.GIFT_BOX.variants.xsmall;
        }
      } else if (adjustedWeightGrams >= PRODUCTS.GIFT_BOX.thresholds[1]) {
        // Large: 11.0 lbs or greater (>= 4989.516 grams)
        if (giftBox.variant_id !== PRODUCTS.GIFT_BOX.variants.large) {
          newGiftBoxVariant = PRODUCTS.GIFT_BOX.variants.large;
        }
      } else if (adjustedWeightGrams >= PRODUCTS.GIFT_BOX.thresholds[0]) {
        // Small: 7.0 to 10.99 lbs (3175.144 to < 4989.516 grams)
        if (giftBox.variant_id !== PRODUCTS.GIFT_BOX.variants.small) {
          newGiftBoxVariant = PRODUCTS.GIFT_BOX.variants.small;
        }
      } else {
        // XSmall: 0.0 to 6.99 lbs (< 3175.144 grams)
        if (giftBox.variant_id !== PRODUCTS.GIFT_BOX.variants.xsmall) {
          newGiftBoxVariant = PRODUCTS.GIFT_BOX.variants.xsmall;
        }
      }

      if (newGiftBoxVariant) {
        updates.push({
          removeKey: giftBox.key,
          addVariant: newGiftBoxVariant,
        });
      }
    }

    if (cooler) {
      let newCoolerVariant = null;
      if (adjustedWeightGrams > PRODUCTS.COOLER.thresholds[2]) {
        // Large: greater than 7257.48 grams
        if (cooler.variant_id !== PRODUCTS.COOLER.variants.large) {
          newCoolerVariant = PRODUCTS.COOLER.variants.large;
        }
      } else if (adjustedWeightGrams > PRODUCTS.COOLER.thresholds[1]) {
        // Medium: 4082.33 to 7252.942 grams
        if (cooler.variant_id !== PRODUCTS.COOLER.variants.medium) {
          newCoolerVariant = PRODUCTS.COOLER.variants.medium;
        }
      } else if (adjustedWeightGrams > PRODUCTS.COOLER.thresholds[0]) {
        // Small: 1360.78 to 4077.795 grams
        if (cooler.variant_id !== PRODUCTS.COOLER.variants.small) {
          newCoolerVariant = PRODUCTS.COOLER.variants.small;
        }
      } else {
        // XSmall: 0 to 1356.241 grams
        if (cooler.variant_id !== PRODUCTS.COOLER.variants.xsmall) {
          newCoolerVariant = PRODUCTS.COOLER.variants.xsmall;
        }
      }

      if (newCoolerVariant) {
        updates.push({
          removeKey: cooler.key,
          addVariant: newCoolerVariant,
        });
      }
    }

    return updates;
  }

  // Helper function to update button states
  function updateButtonStates(cart) {
    const giftBoxButton = document.querySelector("#add-giftbox");
    const coolerButton = document.querySelector("#add-cooler");

    const hasGiftBox = cart.items.some(
      (item) => item.product_id === PRODUCTS.GIFT_BOX.id,
    );
    const hasCooler = cart.items.some(
      (item) => item.product_id === PRODUCTS.COOLER.id,
    );

    if (giftBoxButton) {
      giftBoxButton.style.pointerEvents = hasGiftBox ? "none" : "auto";
      giftBoxButton.style.opacity = hasGiftBox ? "0.3" : "1";
    }
    if (coolerButton) {
      coolerButton.style.pointerEvents = hasCooler ? "none" : "auto";
      coolerButton.style.opacity = hasCooler ? "0.3" : "1";
    }
  }

  // Main update function
  async function updatePackaging() {
    if (isUpdatingPackaging) return;

    const now = Date.now();
    if (now - lastPackagingUpdate < 1000) return;

    lastPackagingUpdate = now;
    isUpdatingPackaging = true;
    pendingCartUpdates = 0;

    showLoadingOverlay();

    try {
      const cart = await getCart();

      updateButtonStates(cart);

      // Auto-clean cart if it only contains cooler and/or gift box
      if (
        !sessionStorage.getItem("cartJustCleaned") &&
        cart.items.length <= 2 &&
        cart.items.every(
          (item) =>
            item.product_id === PRODUCTS.GIFT_BOX.id ||
            item.product_id === PRODUCTS.COOLER.id,
        )
      ) {
        const updates = {};
        cart.items.forEach((item) => (updates[item.key] = 0));
        pendingCartUpdates++;

        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates }),
        });
        invalidateCartCache();

        sessionStorage.setItem("cartJustCleaned", "true");
        window.location.reload();
        return;
      }

      sessionStorage.removeItem("cartJustCleaned");

      // Calculate adjusted weight
      const giftBox = cart.items.find(
        (item) => item.product_id === PRODUCTS.GIFT_BOX.id,
      );
      const cooler = cart.items.find(
        (item) => item.product_id === PRODUCTS.COOLER.id,
      );
      const giftBoxWeight = giftBox ? giftBox.grams * giftBox.quantity : 0;
      const coolerWeight = cooler ? cooler.grams * cooler.quantity : 0;
      const adjustedWeightGrams =
        cart.total_weight - giftBoxWeight - coolerWeight;

      // updateCheckoutButtonState(adjustedWeightGrams);
      // Update weight display in header
      updateWeightDisplay(adjustedWeightGrams);

      const updates = determineNewVariants(cart, adjustedWeightGrams);
      if (updates.length > 0) {
        const cartUpdates = {};

        for (const update of updates) {
          cartUpdates[update.removeKey] = 0;
          cartUpdates[update.addVariant] = 1;
        }

        pendingCartUpdates++;
        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: cartUpdates }),
        });
        invalidateCartCache();

        // Optional: small delay to ensure cart reflects updates
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Verify update
        const updatedCart = await getCart({ force: true });

        const hasAllNew = updates.every((update) =>
          updatedCart.items.some(
            (item) => item.variant_id === update.addVariant,
          ),
        );

        const removedOld = updates.every(
          (update) =>
            !updatedCart.items.some((item) => item.key === update.removeKey),
        );

        document.dispatchEvent(new CustomEvent("cart:updated"));

        if (!hasAllNew || !removedOld) {
          console.warn("Cart update mismatch detected. Reloading page.");
          window.location.reload();
        } else {
          // Force reload to show updated cart even if update worked
          window.location.reload();
        }
      }
    } catch (error) {
      console.error("Error updating packaging:", error);
    } finally {
      isUpdatingPackaging = false;
      setTimeout(hideLoadingOverlay, 500);
    }
  }

  // Auto-add cooler
  async function autoAddCooler() {
    // Already handled in this session
    if (sessionStorage.getItem("coolerAdded")) return;

    try {
      const cart = await getCart();

      // Is cooler already present?
      const hasCooler = cart.items.some(
        (item) => item.product_id === PRODUCTS.COOLER.id,
      );

      // Exclude packaging products from decision logic
      const EXCLUDED_VENDOR = "renard's melt bistro";

      const nonPackagingItems = cart.items.filter((item) => {
        const vendor = (item.vendor || "").trim().toLowerCase();
        return (
          item.product_id !== PRODUCTS.COOLER.id &&
          item.product_id !== PRODUCTS.GIFT_BOX.id &&
          vendor !== EXCLUDED_VENDOR
        );
      });

      // Cooler is required if at least one non-packaging item
      // is NOT product_type "miscellaneous"
      const hasCoolerRequiredItem = nonPackagingItems.some(
        (item) => (item.product_type || "").toLowerCase() !== "miscellaneous",
      );

      // If cart is empty or only contains miscellaneous items, skip cooler.
      // Do NOT set coolerAdded so we can still add later if needed items appear.
      if (!hasCoolerRequiredItem) {
        return;
      }

      // If cooler already exists, mark handled and stop
      if (hasCooler) {
        sessionStorage.setItem("coolerAdded", "true");
        return;
      }

      // Add cooler
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: PRODUCTS.COOLER.variants.xsmall,
          quantity: 1,
        }),
      });
      invalidateCartCache();

      // Mark handled for this session
      sessionStorage.setItem("coolerAdded", "true");

      // Reload to show updated cart
      window.location.reload();
    } catch (error) {
      console.error("Error adding cooler on first visit:", error);
    }
  }

  // Run once on page load
  updatePackaging();
  autoAddCooler();

  // Force cart page reload when cart mutation endpoints are called.
  /** True only when the request affects line items, not note/attributes-only updates */
  function affectsLineItems(url, method = "GET", body) {
    const m = String(method).toUpperCase();
    if (!["POST", "PUT", "PATCH", "DELETE"].includes(m)) return false;

    if (
      url.includes("/cart/add") ||
      url.includes("/cart/change") ||
      url.includes("/cart/clear")
    ) {
      return true;
    }
    if (url.includes("/cart/update")) {
      try {
        const parsed = typeof body === "string" ? JSON.parse(body) : {};
        return !!(
          parsed &&
          typeof parsed.updates === "object" &&
          Object.keys(parsed.updates).length > 0
        );
      } catch {
        return false;
      }
    }
    return false;
  }

  // Force cart page reload when cart mutation endpoints are called (line items only).
  function shouldReloadCartPage(url, method = "GET", body) {
    return (
      window.location.pathname.includes("/cart") &&
      affectsLineItems(url, method, body)
    );
  }

  // Use fetch interception to detect theme cart quantity changes.
  const originalFetch = window.fetch;
  window.fetch = async function (input, init) {
    const requestUrl =
      typeof input === "string"
        ? input
        : input && typeof input.url === "string"
          ? input.url
          : "";
    const requestMethod =
      (init && init.method) ||
      (input && typeof input !== "string" && input.method) ||
      "GET";
    const requestBody =
      (init && init.body) ?? (input && typeof input !== "string" && input.body);

    const response = await originalFetch.apply(this, arguments);

    if (shouldReloadCartPage(requestUrl, requestMethod, requestBody)) {
      window.location.reload();
    }

    return response;
  };

  // Use XMLHttpRequest to listen for cart updates
  const originalXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._method = method;
    this._url = url;
    return originalXHROpen.apply(this, arguments);
  };

  const originalXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (body) {
    this._body = body;
    this.addEventListener("load", function () {
      if (
        this._url &&
        this._url.includes("/cart") &&
        !this._url.includes("/cart.js")
      ) {
        if (shouldReloadCartPage(this._url, this._method, this._body)) {
          window.location.reload();
          return;
        }

        if (
          !isUpdatingPackaging &&
          affectsLineItems(this._url, this._method, this._body)
        ) {
          updatePackaging();
        }
      }
    });
    return originalXHRSend.apply(this, arguments);
  };

  // Add event listener for third-party cart updates
  document.addEventListener("cart:updated", () => {
    if (!isUpdatingPackaging) {
      updatePackaging();
    }
    // updateCheckoutButtonState(getAdjustedCartWeight());
  });

  // Add keyframes for spinner animation
  const style = document.createElement("style");
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
});
